import { jsx, jsxs } from 'react/jsx-runtime';
import React__default, { forwardRef, useMemo } from 'react';
import classNames from 'classnames';
import { useSelect } from 'downshift';
import isEqual from 'lodash.isequal';
import { Input } from '../Input/Input.es.js';
import { DropdownOptions } from './Dropdown.Options/Dropdown.Options.es.js';

function DropdownCore({ items, error, warning, label, labelSuffix, display, placeholder, message, className, onChange, onBlur, onStateChange, value, name, disabled, menuLoading = false, inputLoading = false, dropDirection = 'down', variant = 'default', intent = 'default', children, ...rest }, ref) {
    // Derived selected item
    const derivedSelectedItem = useMemo(()=>items.filter((item)=>!item.header).find((item)=>{
            return isEqual(value, item.value);
        }) ?? null, [
        items,
        value
    ]);
    const { isOpen, selectedItem, getToggleButtonProps, getLabelProps, getMenuProps, getItemProps, highlightedIndex, openMenu, setHighlightedIndex } = useSelect({
        items,
        selectedItem: derivedSelectedItem,
        onStateChange: (changes)=>{
            // if the wrapping component passed in an onStateChange function,
            // they probably want to do custom logic
            if (onStateChange) {
                return onStateChange(changes);
            }
            const { type, isOpen, selectedItem, inputValue } = changes;
            switch(type){
                case useSelect.stateChangeTypes.ToggleButtonClick:
                    if (!isOpen) {
                        // user is closing the menu by clicking on the DropDown button,
                        // we can consider this as the user completing "touching" the DropDown
                        onBlur?.();
                    }
                    break;
                case useSelect.stateChangeTypes.MenuBlur:
                case useSelect.stateChangeTypes.MenuKeyDownEscape:
                    // user has finished interacting with the DropDown without making any new selections
                    onBlur?.();
                    break;
                case useSelect.stateChangeTypes.MenuKeyDownEnter:
                    if (selectedItem == null) {
                        // edge case: user clicked on an enter key with none of the options highlighted
                        onBlur?.();
                    } else {
                        // user has made a selection
                        onChange?.(selectedItem);
                    }
                    break;
                case useSelect.stateChangeTypes.ToggleButtonKeyDownCharacter:
                case useSelect.stateChangeTypes.MenuKeyDownCharacter:
                    // If menu isn't open yet, ensure it opens to show keyboard selected item
                    if (!isOpen) {
                        openMenu();
                    }
                    // Support keyboard input select
                    if (inputValue) {
                        const firstMatchingIndex = items.findIndex((item)=>{
                            return item.label.toLowerCase().startsWith(inputValue.toLowerCase());
                        });
                        setHighlightedIndex(firstMatchingIndex);
                    }
                    break;
                case useSelect.stateChangeTypes.ItemClick:
                    // user has made a selection
                    if (selectedItem != null) {
                        onChange?.(selectedItem);
                    }
                    break;
                case useSelect.stateChangeTypes.MenuKeyDownArrowDown:
                case useSelect.stateChangeTypes.MenuKeyDownArrowUp:
                case useSelect.stateChangeTypes.MenuKeyDownHome:
                case useSelect.stateChangeTypes.MenuKeyDownEnd:
                case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
                case useSelect.stateChangeTypes.MenuMouseLeave:
                case useSelect.stateChangeTypes.ItemMouseMove:
                case useSelect.stateChangeTypes.ToggleButtonKeyDownArrowDown:
                case useSelect.stateChangeTypes.ToggleButtonKeyDownArrowUp:
                case useSelect.stateChangeTypes.FunctionToggleMenu:
                case useSelect.stateChangeTypes.FunctionOpenMenu:
                case useSelect.stateChangeTypes.FunctionCloseMenu:
                case useSelect.stateChangeTypes.FunctionSetHighlightedIndex:
                case useSelect.stateChangeTypes.FunctionSelectItem:
                case useSelect.stateChangeTypes.FunctionSetInputValue:
                case useSelect.stateChangeTypes.FunctionReset:
                    break;
            }
        }
    });
    const dropdownClasses = classNames('cb-dropdown', `variant-${variant}`, {
        focused: isOpen,
        error,
        disabled,
        'no-label': !label,
        'input-loading': inputLoading,
        'no-selection': selectedItem === null,
        'no-placeholder': placeholder == null
    }, className);
    const menuContainerCss = classNames('dropdown-menu-container', `drop-direction-${dropDirection}`, {
        open: isOpen
    });
    return jsx("div", {
        className: dropdownClasses,
        "data-testid": "dropdown-container",
        title: label,
        ...rest,
        children: jsxs(Input, {
            disabled: disabled,
            display: display,
            emphasize: intent === 'filter',
            error: error,
            label: label,
            labelProps: getLabelProps(),
            labelSuffix: labelSuffix,
            loading: inputLoading,
            message: message,
            placeholder: placeholder,
            value: selectedItem?.label,
            warning: warning,
            children: [
                jsxs(Input.Field.Button, {
                    ...getToggleButtonProps({
                        ref
                    }),
                    focused: isOpen,
                    name: name,
                    children: [
                        selectedItem?.icon && jsx(Input.Inline.Icon, {
                            "data-testid": "left-icon",
                            name: selectedItem.icon
                        }),
                        !isOpen && selectedItem?.tagIcon != null && jsx(Input.Inline.Icon, {
                            align: "right",
                            name: selectedItem.tagIcon
                        }),
                        !isOpen && selectedItem?.tag && jsx(Input.Inline.Affix, {
                            variant: "suffix",
                            children: selectedItem.tag
                        }),
                        jsx(Input.Inline.Icon, {
                            align: "right",
                            className: "caret",
                            name: "ChevronDownSolid",
                            size: 20
                        })
                    ]
                }),
                jsx("div", {
                    className: menuContainerCss,
                    "data-testid": "menu-container",
                    children: /*#__PURE__*/ React__default.isValidElement(children) && /*#__PURE__*/ React__default.cloneElement(children, {
                        loading: menuLoading,
                        getMenuProps,
                        getItemProps,
                        highlightedIndex,
                        selected: selectedItem ?? undefined
                    })
                })
            ]
        })
    });
}
const DropdownForwardRef = /*#__PURE__*/ forwardRef(DropdownCore);
DropdownForwardRef.displayName = 'Dropdown';
DropdownForwardRef.Options = DropdownOptions;
DropdownForwardRef.stateChangeTypes = useSelect.stateChangeTypes;
const Dropdown = DropdownForwardRef;

export { Dropdown, Dropdown as default };
//# sourceMappingURL=Dropdown.es.js.map
